import { useLocalization } from "@fluent/react";
import { IconButton, TruncatedAccount } from "@narmi/design_system";
import filters from "byzantine/src/filters";
import { catcat as cc } from "cerulean";
import logoStyles from "../../../PreviewCard/Logo.module.scss";
import { getRecipientById } from "./BulkPaymentTable";
import styles from "./BulkPaymentTable.module.scss";

type SelectedRecipientCardType = {
  recipientId: API.RecipientId;
  rowIndex: number;
  form?: any;
  recipients: API.Recipient[];
  recipient?: API.Recipient;
  handleClickEditRecipient?: (
    recipientId: API.RecipientId,
    rowIndex?: number
  ) => void;
  handleClickUnsavedRecipient?: (recipient: API.Recipient, rowIndex: number) => void;
  staticCard?: boolean;
  error?: boolean;
};

const SelectedRecipientCard = ({
  recipientId,
  rowIndex,
  form,
  recipients,
  recipient: fallbackRecipient,
  handleClickEditRecipient,
  handleClickUnsavedRecipient,
  staticCard,
  error,
}: SelectedRecipientCardType) => {
  const { l10n } = useLocalization();
  const storeRecipient = getRecipientById(recipients, recipientId);
  const recipient = storeRecipient ?? fallbackRecipient;
  if (!recipient) return null;

  // The recipients list only contains non-hidden recipients, so if we
  // found the recipient there it is definitely saved.  Fall back to the
  // form-state object's `hidden` flag only when the store doesn't have it.
  const isUnsaved = !storeRecipient && recipient.hidden === true;
  const nameClickable = isUnsaved && handleClickUnsavedRecipient;

  return (
    <div
      className={cc(styles.selectedRecipientCardContainer, {
        [styles.interactive]: !staticCard,
        [styles.cellError]: error,
      })}
    >
      <div className={styles.nameContainer}>
        {nameClickable ? (
          <button
            type="button"
            className={cc(styles.nameEllipsis, styles.nameButton)}
            onClick={() => handleClickUnsavedRecipient(recipient, rowIndex)}
          >
            {recipient.name}
          </button>
        ) : (
          <span className={styles.nameEllipsis}>{recipient.name}</span>
        )}
        <div className={styles.institutionNameContainer}>
          <div className={logoStyles.logo}>
            <span className="narmi-icon-bank" />
          </div>
          <span className="fontColor--secondary fontSize--xs">
            <TruncatedAccount
              lastFour={
                recipient.ach_destination?.account_number?.slice(-4) ?? ""
              }
              name={filters.capitalizeFirstLetter(
                recipient.ach_destination?.account_type ?? ""
              )}
            />
          </span>
        </div>
      </div>
      <div className={styles.iconsContainer}>
        {!staticCard && handleClickEditRecipient ? (
          <>
            <IconButton
              kind="action"
              name="edit"
              label={l10n.getString("bulk-payments-table-edit-recipient", {
                name: recipient.name,
              })}
              onClick={() => {
                if (isUnsaved && handleClickUnsavedRecipient) {
                  handleClickUnsavedRecipient(recipient, rowIndex);
                } else {
                  handleClickEditRecipient(recipientId);
                }
              }}
            />
            <IconButton
              kind="action"
              name="x"
              label={l10n.getString("bulk-payments-table-clear-recipient", {
                name: recipient.name,
              })}
              onClick={() => {
                form.setFieldValue(
                  `payments.${rowIndex}.recipient`,
                  undefined
                );
                form.setFieldValue(
                  `payments.${rowIndex}.speedStatus`,
                  ""
                );
                form.setFieldValue(
                  `payments.${rowIndex}.speedStatusWithExpediteOption`,
                  ""
                );
              }}
            />
          </>
        ) : null}
      </div>
    </div>
  );
};

export default SelectedRecipientCard;
